/*
Muestra la página de creacion de Cotizaciones, 
e invoca al preview
Autor: Benjamín León
Fecha: 2024-09-19
*/
import React, { useEffect, useState } from 'react'

import EstimateDto from '../interfaces/EstimateDto'
import UserData from '../interfaces/UserData'
import { useAuth } from '../hooks/useAuth'
import { formatCurrency, formatDateToYYYY_MM_DD } from '../utils/utils'
import Vehicle from '../interfaces/Vehicle'
import Client from '../interfaces/Client'
import Deal from '../interfaces/Deal'

import CheckboxField from './CheckBoxField'
import InputTextArea from './InputTextArea'
import InputField from './InputField'
import EstimatePreview from './EstimatePreview'
import { ButtonAction } from './Buttons'

const EstimateEdit: React.FC<{
  vehicle: Vehicle
  client: Client
  onCreate?: (id: string) => void
  deal?: Deal
}> = ({ vehicle, client, onCreate, deal }) => {
  const { userData } = useAuth()
  const [formData, setFormData] = useState<EstimateDto | null>(null)
  const [showPreview, setShowPreview] = useState<boolean>(false)

  useEffect(() => {
    setFormData(initEstimate(userData, null, vehicle, client, deal))
  }, [userData, vehicle, client, deal])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target

    if (formData) {
      setFormData((prevFormData) => {
        let updatedValue: string | number | Date = value

        if (type === 'number') {
          updatedValue = Number(value)
        }

        if (name !== 'salePrice_format') {
          return {
            ...prevFormData,
            [name]: updatedValue,
          }
        }

        let numericValue = value.replace(/[^\d]/g, '') // Strip non-numeric characters

        if (!numericValue) {
          numericValue = '0'
        }

        // Calculate the discount if the salePrice or price changes
        const salePrice = Number(numericValue)
        const salePrice_format = new Intl.NumberFormat().format(salePrice)
        const price = prevFormData?.price ? Number(prevFormData.price) : 0
        const discount = price > 0 && salePrice < price ? (100 * (price - salePrice)) / price : 0

        // Format discount using the locale's decimal separator
        const discount_format = discount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })

        return {
          ...prevFormData,
          salePrice: salePrice,
          salePrice_format: salePrice_format,
          discount: discount,
          discount_format: discount_format,
        }
      })
    }
  }
  // Handle checkbox change
  const handleCheckDiscountShow = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target
    setFormData((prevFormData) => {
      return { ...prevFormData, discountHide: !checked }
    })
    //  setDiscountShow(checked)
  }

  const handleEditClick = async () => {
    setShowPreview(true)
  }
  // The callback to go back to the edit view
  const handleGoBack = () => {
    setShowPreview(false)
  }

  return (
    <div className="p-4">
      {!showPreview ? (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <InputField
                label="Número"
                name="number"
                value={formData?.number}
                type="number"
                handleInputChange={handleInputChange}
              />
              <div className="text-sm">Deja en cero para numeración automática</div>
            </div>
            <DisplayField label="Precio" value={formData?.price_format} />
            <InputField
              label="Precio de Venta"
              name="salePrice_format"
              value={formData?.salePrice_format}
              type="string"
              handleInputChange={handleInputChange}
            />
            <DisplayFieldAndCheck
              label="% Descuento"
              value={formData?.discount_format}
              checked={!formData?.discountHide}
              handleInputChange={handleCheckDiscountShow}
            />
            <InputField
              label="Fecha"
              name="date"
              value={formData?.date}
              type="date"
              handleInputChange={handleInputChange}
            />
            <InputField
              label="Expiración"
              name="expiresAt"
              value={formData?.expiresAt}
              type="date"
              handleInputChange={handleInputChange}
            />
          </div>
          <InputTextArea
            label="Observaciones"
            name="notes"
            value={formData?.notes}
            handleInputChange={handleInputChange}
          />

          <div className="flex justify-center mt-4">
            <ButtonAction label="Vista Previa" handleButtonClick={handleEditClick} />
          </div>
        </div>
      ) : (
        <EstimatePreview
          estimate={formData}
          onGoBack={handleGoBack}
          onCreate={onCreate}
          vehicle={vehicle}
          client={client}
          deal={deal}
        />
      )}
    </div>
  )
}

const DisplayField: React.FC<{ label: string; value?: string | number }> = ({ label, value }) => {
  return (
    <div className="flex flex-col">
      <label className="font-semibold mb-1">{label}</label>
      <div className="border rounded p-2 bg-gray-100 text-gray-600">{value}</div>
    </div>
  )
}
const DisplayFieldAndCheck: React.FC<{
  label: string
  value?: string | number
  checked: boolean
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}> = ({ label, value, checked, handleInputChange }) => {
  return (
    <div className="flex flex-row">
      <div className="flex flex-col">
        <label className="font-semibold mb-1">{label}</label>
        <div className="border rounded p-2 bg-gray-100 text-gray-600">{checked ? value : '\xA0'}</div>
      </div>
      <div className="ml-4">
        <CheckboxField name="ver" value="" label="Ver" checked={checked} handleInputChange={handleInputChange} />
      </div>
    </div>
  )
}

const initEstimate = (
  userData: UserData | null,
  estimate: EstimateDto | null,
  vehicle: Vehicle,
  client: Client,
  deal?: Deal
) => {
  const today = new Date()
  const today_format = formatDateToYYYY_MM_DD(today)
  const expDate = new Date()
  expDate.setDate(expDate.getDate() + 15)
  const expDate_format = formatDateToYYYY_MM_DD(expDate)
  const price = vehicle.price ?? 0
  const price_format = formatCurrency(price)
  const discount = 100
  const discount_format = discount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return {
    number: estimate?.number ?? 0,
    price: price,
    price_format: estimate?.price_format ?? price_format,
    salePrice: estimate?.salePrice ?? 0,
    salePrice_format: estimate?.salePrice_format ?? '',
    discount: estimate?.discount ?? discount,
    discount_format: estimate?.discount_format ?? discount_format,
    date: estimate?.date ?? today_format,
    expiresAt: estimate?.expiresAt ?? expDate_format,
    notes: estimate?.notes ?? '',
    organization: userData?.organization?._id ?? '',
    profile: deal?.profile ?? userData?.profile?._id ?? '',
    client: client._id,
    vehicle: vehicle._id,
  }
}
export default EstimateEdit
